<template>
  <div class="w-full p-6">
    <div class="w-full p-6 bg-white rounded-lg shadow">
      <h3 class="text-lg font-semibold">{{ $t('widgets.title') }}</h3>
      <p class="text-sm text-grey">{{ $t('widgets.description') }}</p>

      <div class="mt-4">
        <LabelForm>{{ $t('widgets.resort.label') }}</LabelForm>
        <div v-if="resorts.length > 1" class="flex flex-row gap-4 items-end">
          <Combobox
            v-model="selectedResortUuid"
            class="w-1/2"
            :options="resorts.map(resort => ({ key: resort.uuid, text: resort.name }))"
            :placeholder="$t('widgets.resort.placeholder')"
          />
          <button class="sk-btn sk-btn-primary rounded-lg" @click="confirmResort" :disabled="!selectedResortUuid">
            {{ $t('widgets.resort.confirm') }}
          </button>
        </div>
        <div v-else class="text-sm">
          <span v-if="resorts.length === 1">{{ resorts[0].name }}</span>
          <span v-else>{{ $t('widgets.resort.none') }}</span>
        </div>
      </div>
    </div>

    <div v-if="resortConfirmed" class="mt-6 flex flex-col gap-6">
      <div class="w-full p-6 bg-white rounded-lg shadow">
        <h4 class="text-base font-semibold">{{ $t('widgets.sections.data_origin.title') }}</h4>
        <p class="text-sm text-grey">{{ $t('widgets.sections.data_origin.description') }}</p>
        <div class="mt-4">
          <LabelForm>{{ $t('widgets.sections.data_origin.label') }}</LabelForm>
          <Combobox
            v-model="dataOrigin"
            class="w-1/2"
            :options="dataOriginOptions"
            :placeholder="$t('widgets.sections.data_origin.placeholder')"
          />
        </div>
      </div>

      <SnowReportSection
        v-model="selectedFields"
        :selected-resort-uuid="selectedResortUuid"
        :data-origin="dataOrigin"
      />

      <CheckDataPanels
        :selected-resort-uuid="selectedResortUuid"
        :resort-info="resortInfo"
      />

      <div class="w-full p-6 bg-white rounded-lg shadow">
        <button class="sk-btn sk-btn-primary rounded-lg" @click="saveSettings">
          {{ $t('widgets.actions.save') }}
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import AppsService from '@/modules/Apps/Services/AppsService'
import WidgetsService from '@/modules/Apps/Services/WidgetsService'
import ResortsServices from '@/modules/Resorts/Services/ResortsServices'
import SnowReportSettingsService from '@/modules/Apps/Services/SnowReportSettingsService'
import { unifyPaginatedResponse } from '@/modules/Shared/Helpers/apiResponseHelper'
import Combobox from '@/modules/Shared/Components/Combobox.vue'
import LabelForm from '@/modules/Shared/Components/form/LabelForm'
import CheckDataPanels from '@/modules/Apps/Pages/widgets/components/CheckDataPanels.vue'
import SnowReportSection from '@/modules/Apps/Pages/widgets/components/SnowReportSection.vue'
import loader from '@/modules/Shared/Mixins/loader'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'widgets-configuration',
  mixins: [loader],
  components: {
    Combobox,
    LabelForm,
    CheckDataPanels,
    SnowReportSection
  },
  data() {
    return {
      applicationUuid: '',
      resorts: [],
      selectedResortUuid: '',
      resortConfirmed: false,
      resortInfo: null,
      zones: [],
      dataOrigin: 'general_report',
      selectedFields: [],
      snowReportUpdatedAt: null
    }
  },
  computed: {
    dataOriginOptions() {
      return [
        { key: 'general_report', text: this.$t('widgets.sections.data_origin.general') },
        ...this.zones.map(zone => ({ key: String(zone.id), text: zone.zone_name }))
      ]
    },
    ...mapGetters('resorts', ['resortInfoByUuid']),
  },
  async created() {
    this.applicationUuid = this.$route.params.uuid
    this.resorts = await AppsService.getResorts(this.applicationUuid)
    if (this.resorts.length === 1) {
      this.selectedResortUuid = this.resorts[0].uuid
      await this.confirmResort()
    }
  },
  methods: {
    ...mapActions('resorts', ['updateResortInfo']),
    async confirmResort() {
      if (!this.selectedResortUuid) {
        return
      }
      const success = await this.loadAndIgnoreError(async () => {
        this.resortConfirmed = true
        await this.loadResortInfo()
        await this.loadWidgetSettings()
        await this.loadZones()
      })
      if (!success) {
        this.notifyError(this.$t('shops.error.0'), this.$t('widgets.notifications.load_error'))
      }
    },
    async loadResortInfo() {
      try {
        await this.updateResortInfo()
        this.resortInfo = this.resortInfoByUuid(this.selectedResortUuid)
      } catch (error) {
        try {
          const resortsInfo = await ResortsServices.getResortsInfo()
          this.resortInfo = resortsInfo.find(resort => resort.uuid === this.selectedResortUuid) || null
        } catch (innerError) {
          this.resortInfo = null
        }
      }
    },
    async loadWidgetSettings() {
      try {
        const settings = await WidgetsService.getWidgetSettings(this.selectedResortUuid)
        const safeSettings = settings || {}
        this.dataOrigin = safeSettings.data_origin || 'general_report'
        this.selectedFields = Array.isArray(safeSettings.fields) ? safeSettings.fields : []
      } catch (error) {
        this.dataOrigin = 'general_report'
        this.selectedFields = []
      }
    },
    async loadZones() {
      const zones = await unifyPaginatedResponse(SnowReportSettingsService.getSnowReportZones, this.selectedResortUuid)
      this.zones = zones || []
    },
    async saveSettings() {
      let response = null
      const success = await this.loadAndIgnoreError(async () => {
        const payload = {
          data_origin: this.dataOrigin,
          fields: this.selectedFields
        }
        response = await WidgetsService.updateWidgetSettings(this.selectedResortUuid, payload)
      })

      if (!success || !response || response.code !== 200) {
        this.notifyError(this.$t('shops.error.0'), this.$t('widgets.notifications.save_error'))
        return
      }
      this.notifySuccess(this.$t('shops.success'), this.$t('widgets.notifications.save_ok'))
    },
  }
}
</script>
